import React from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { Button } from '../../components/ui/Button';
import { getProgramById } from '../../data/programs';
import { Trophy, Target, Zap, Award } from 'lucide-react';
export function CompetitionTeamPage() {
  const program = getProgramById('competition-team');
  if (!program) {
    return null;
  }
  return <div className="min-h-screen bg-white font-sans text-slate-900" data-id="element-1068">
      <Header data-id="element-1069" />

      <main className="pt-20" data-id="element-1070">
        {/* Hero Section with Background Image */}
        <section className="relative py-24 md:py-32 overflow-hidden" data-id="element-1071">
          <div className="absolute inset-0 z-0" data-id="element-1072">
            <img src={program.image} alt={program.name} className="w-full h-full object-cover" data-id="element-1073" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/75" data-id="element-1074"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1075">
            <div className="max-w-3xl" data-id="element-1076">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-1077">
                {program.name}
              </h1>
              <p className="text-xl text-slate-200 mb-8 leading-relaxed" data-id="element-1078">
                Elite training for dedicated athletes ready to test their skills
                on the competition stage. Push your limits and achieve
                excellence.
              </p>
              <Button variant="primary" size="lg" className="text-lg" data-id="element-1079">
                Join the Competition Team
              </Button>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-16 bg-slate-50" data-id="element-1080">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1081">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1082">
              <div className="text-center" data-id="element-1083">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#dc2626] rounded-full mb-4" data-id="element-1084">
                  <Trophy className="w-8 h-8 text-white" data-id="element-1085" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-2" data-id="element-1086">
                  Tournament Success
                </h3>
                <p className="text-slate-600" data-id="element-1087">
                  Compete at local, regional, and national tournaments
                </p>
              </div>

              <div className="text-center" data-id="element-1088">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#dc2626] rounded-full mb-4" data-id="element-1089">
                  <Target className="w-8 h-8 text-white" data-id="element-1090" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-2" data-id="element-1091">
                  Advanced Training
                </h3>
                <p className="text-slate-600" data-id="element-1092">
                  Specialized coaching focused on competitive techniques
                </p>
              </div>

              <div className="text-center" data-id="element-1093">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#dc2626] rounded-full mb-4" data-id="element-1094">
                  <Zap className="w-8 h-8 text-white" data-id="element-1095" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-2" data-id="element-1096">
                  Peak Performance
                </h3>
                <p className="text-slate-600" data-id="element-1097">
                  Mental and physical conditioning for competitive excellence
                </p>
              </div>

              <div className="text-center" data-id="element-1098">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#dc2626] rounded-full mb-4" data-id="element-1099">
                  <Award className="w-8 h-8 text-white" data-id="element-1100" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-2" data-id="element-1101">
                  Elite Community
                </h3>
                <p className="text-slate-600" data-id="element-1102">
                  Train alongside the most dedicated athletes in our school
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-white" data-id="element-1103">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1104">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-6" data-id="element-1105">
              Where Champions Are Forged
            </h2>

            <div className="prose prose-lg max-w-none text-slate-700 space-y-6" data-id="element-1106">
              <p data-id="element-1107">
                The Competition Team isn't for everyone—and that's exactly the
                point. This program is designed for students who want more than
                just regular training. You're ready to push yourself harder,
                train smarter, and test your skills against the best martial
                artists in the region and beyond. If you're driven by the
                pursuit of excellence and thrive on challenge, this is where you
                belong.
              </p>

              <p data-id="element-1108">
                Competition changes everything. It takes your training to a
                level that's simply impossible to reach in regular classes. When
                you step onto that mat with a competitor across from you,
                everything you've learned gets tested under real pressure. Your
                technique, your conditioning, your mental toughness, your
                strategy—it all comes together in those intense moments. And
                whether you win or lose, you walk away better than you were
                before.
              </p>

              <p data-id="element-1109">
                Our Competition Team training is structured to give you every
                advantage. You'll receive specialized coaching that goes beyond
                basic techniques, focusing on the strategies, combinations, and
                game plans that win matches. We analyze your strengths and
                weaknesses, develop personalized training plans, and prepare you
                mentally and physically for the demands of competition. You'll
                learn how to read opponents, capitalize on opportunities, and
                perform under pressure.
              </p>

              <p data-id="element-1110">
                The physical demands are intense, but that's where growth
                happens. Competition training pushes your cardiovascular
                endurance, explosive power, speed, and technical precision to
                new levels. You'll develop the kind of conditioning that makes
                regular training feel easy. More importantly, you'll build
                mental toughness—the ability to stay calm when you're tired, to
                execute technique when your opponent is fighting back, and to
                push through when everything in you wants to quit.
              </p>

              <p data-id="element-1111">
                Being part of the Competition Team means joining an elite
                community of athletes who share your dedication and drive.
                You'll train alongside students who push you to be better, who
                understand the commitment required, and who celebrate your
                victories as their own. The bonds formed through competition
                training are unlike anything else—there's a special respect that
                comes from pushing each other to your limits and supporting each
                other through the challenges.
              </p>

              <p data-id="element-1112">
                We compete at tournaments throughout Michigan and beyond,
                representing Che Ko Martial Arts with pride and excellence. Our
                competition team has a strong track record of success, with
                students regularly placing at local, regional, and national
                events. But more than the medals and trophies, our competitors
                develop qualities that serve them for life: discipline,
                resilience, confidence, and the knowledge that they can achieve
                anything they set their mind to.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#dc2626] to-[#b91c1c] text-white" data-id="element-1113">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1114">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1115">
              Are You Ready to Compete at the Highest Level?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1116">
              The Competition Team is accepting dedicated students who are ready
              to commit to excellence. If you're prepared to train harder, push
              further, and test yourself against the best, we want to hear from
              you.
            </p>
            <Button variant="secondary" size="lg" className="text-lg shadow-xl" data-id="element-1117">
              Apply to Join the Team
            </Button>
            <p className="text-sm text-white/80 mt-4" data-id="element-1118">
              Instructor approval required • Additional training commitment •
              Tournament participation expected
            </p>
          </div>
        </section>
      </main>

      <Footer data-id="element-1119" />
    </div>;
}